import { useEffect } from "react";
import mixpanel from "mixpanel-browser";
import { useCookies } from "react-cookie";

import { MIXPANEL_COOKIE_NAME, MIXPANEL_COOKIE_EXPIRATION } from "config/cookies";
import config from "config/config";
import { extractDomain, getExpDateByDays } from "utils//cookiesUtils";
import { trackMixpanel } from "utils/trackingUtils";
import {
  deviceType,
  deviceModel,
  viewportHeight,
  viewportWidth,
  pagePath,
  pageReferer,
  pageUrl,
} from "utils/browserUtils";
import type { GetMixpanelDataProps, InitializeTrackingProps, MixpanelData, UseMixpanelProps } from "./useMixpanel.d";

export const webTheme = () => {
  let theme = "Desktop";
  if (viewportWidth() < 480) {
    theme = "Phone";
  } else if (viewportWidth() < 768) {
    theme = "Tablet";
  }

  return theme;
};

export const getMixpanelData = (
  language: GetMixpanelDataProps["language"],
  pageType: GetMixpanelDataProps["pageType"],
  pageTitle: GetMixpanelDataProps["pageTitle"]
): MixpanelData => {
  const mixpanelData = {
    deviceModel: deviceModel(),
    deviceType: deviceType(),
    pageLanguage: language,
    pagePath: pagePath(),
    pageReferer: pageReferer(),
    pageTitle,
    pageType,
    pageUrl: pageUrl(),
    webTheme: webTheme(),
    viewportWidth: viewportWidth(),
    viewportHeight: viewportHeight(),
  };

  return mixpanelData;
};

const initializeTracking = (
  cookies: InitializeTrackingProps["cookies"],
  setCookie: InitializeTrackingProps["setCookie"]
) => {
  mixpanel.init(config.mixpanelToken, {
    loaded() {
      const persistedDistinctId = cookies[MIXPANEL_COOKIE_NAME];
      const mixpanelDistinctId = mixpanel.get_distinct_id();

      if (persistedDistinctId) {
        mixpanel.identify(persistedDistinctId);
      } else if (!persistedDistinctId && mixpanelDistinctId) {
        setCookie(MIXPANEL_COOKIE_NAME, mixpanelDistinctId, {
          path: "/",
          expires: getExpDateByDays(MIXPANEL_COOKIE_EXPIRATION),
          domain: extractDomain(),
        });
      }
    },
    track_pageview: false,
    persistence: "localStorage",
  });
};

const trackPageViewed = (language: UseMixpanelProps["language"], pageType: UseMixpanelProps["pageType"]) => {
  const { title } = document;
  const mixpanelOptions = getMixpanelData(language, pageType, title);
  trackMixpanel("pageViewed", mixpanelOptions);
};

const useMixpanel = (language: UseMixpanelProps["language"], pageType: UseMixpanelProps["pageType"]) => {
  const [cookies, setCookie] = useCookies([MIXPANEL_COOKIE_NAME]);

  useEffect(() => {
    initializeTracking(cookies, setCookie);
    trackPageViewed(language, pageType);
  }, [cookies, setCookie, language, pageType]);
};

export default useMixpanel;
